import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilterBar = ({ onFiltersChange }) => {
  const [activeFilters, setActiveFilters] = useState({
    dateRange: '7d',
    trafficSource: [],
    geography: [],
    status: 'all',
    savedView: null
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const dateRanges = [
    { value: '1d', label: 'Today' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'custom', label: 'Custom range' }
  ];

  const trafficSources = [
    { value: 'social', label: 'Social Media', count: 234 },
    { value: 'search', label: 'Search Engine', count: 189 },
    { value: 'tv', label: 'TV Commercial', count: 156 },
    { value: 'radio', label: 'Radio', count: 123 },
    { value: 'direct_mail', label: 'Direct Mail', count: 98 },
    { value: 'video', label: 'Video Ads', count: 67 }
  ];

  const geographicRegions = [
    { value: 'ca', label: 'California', count: 345 },
    { value: 'tx', label: 'Texas', count: 289 },
    { value: 'fl', label: 'Florida', count: 234 },
    { value: 'ny', label: 'New York', count: 198 },
    { value: 'il', label: 'Illinois', count: 156 },
    { value: 'oh', label: 'Ohio', count: 134 }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Campaigns' },
    { value: 'active', label: 'Active Only' },
    { value: 'paused', label: 'Paused Only' },
    { value: 'stopped', label: 'Stopped Only' }
  ];

  const savedViews = [
    { id: 'high_performers', name: 'High Performers', filters: { eRPC: '>15', winRate: '>80' } },
    { id: 'new_campaigns', name: 'New Campaigns', filters: { created: '<7d' } },
    { id: 'low_conversion', name: 'Low Conversion', filters: { conversionRate: '<20' } },
    { id: 'premium_traffic', name: 'Premium Traffic', filters: { avgBid: '>15' } }
  ];

  const handleFilterChange = (filterType, value) => {
    let newFilters = { ...activeFilters };

    if (filterType === 'trafficSource' || filterType === 'geography') {
      const currentValues = newFilters?.[filterType];
      if (currentValues?.includes(value)) {
        newFilters[filterType] = currentValues?.filter(v => v !== value);
      } else {
        newFilters[filterType] = [...currentValues, value];
      }
    } else {
      newFilters[filterType] = value;
    }

    setActiveFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      dateRange: '7d',
      trafficSource: [],
      geography: [],
      status: 'all',
      savedView: null
    };
    setActiveFilters(clearedFilters);
    if (onFiltersChange) {
      onFiltersChange(clearedFilters);
    }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilters?.dateRange !== '7d') count++;
    if (activeFilters?.trafficSource?.length > 0) count++;
    if (activeFilters?.geography?.length > 0) count++;
    if (activeFilters?.status !== 'all') count++;
    if (activeFilters?.savedView) count++;
    return count;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Quick Filters */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          {/* Date Range */}
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <select
              value={activeFilters?.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e?.target?.value)}
              className="bg-input border border-border rounded px-3 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {dateRanges?.map((range) => (
                <option key={range?.value} value={range?.value}>
                  {range?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={16} className="text-muted-foreground" />
            <select
              value={activeFilters?.status}
              onChange={(e) => handleFilterChange('status', e?.target?.value)}
              className="bg-input border border-border rounded px-3 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {statusOptions?.map((status) => (
                <option key={status?.value} value={status?.value}>
                  {status?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Saved Views */}
          <div className="flex items-center space-x-2">
            <Icon name="Bookmark" size={16} className="text-muted-foreground" />
            <select
              value={activeFilters?.savedView || ''}
              onChange={(e) => handleFilterChange('savedView', e?.target?.value || null)}
              className="bg-input border border-border rounded px-3 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Saved Views</option>
              {savedViews?.map((view) => (
                <option key={view?.id} value={view?.id}>
                  {view?.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {getActiveFilterCount() > 0 && (
            <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
              {getActiveFilterCount()} active
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            Advanced Filters
          </Button>
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              iconName="X"
            >
              Clear All
            </Button>
          )}
        </div>
      </div>
      {/* Advanced Filters */}
      {isExpanded && (
        <div className="border-t border-border pt-4 space-y-4">
          {/* Traffic Source Filter */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Traffic Source</h4>
            <div className="flex flex-wrap gap-2">
              {trafficSources?.map((source) => (
                <button
                  key={source?.value}
                  onClick={() => handleFilterChange('trafficSource', source?.value)}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm transition-smooth ${
                    activeFilters?.trafficSource?.includes(source?.value)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  <span>{source?.label}</span>
                  <span className="text-xs opacity-75">({source?.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Geographic Filter */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Geography</h4>
            <div className="flex flex-wrap gap-2">
              {geographicRegions?.map((region) => (
                <button
                  key={region?.value}
                  onClick={() => handleFilterChange('geography', region?.value)}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm transition-smooth ${
                    activeFilters?.geography?.includes(region?.value)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  <span>{region?.label}</span>
                  <span className="text-xs opacity-75">({region?.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Min eRPC ($)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                className="w-full px-3 py-2 bg-input border border-border rounded text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Min Win Rate (%)
              </label>
              <input
                type="number"
                step="1"
                placeholder="0"
                className="w-full px-3 py-2 bg-input border border-border rounded text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Min Call Volume
              </label>
              <input
                type="number"
                step="1"
                placeholder="0"
                className="w-full px-3 py-2 bg-input border border-border rounded text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;